import type { Study, BritProtocol } from '../../../api/types';

// ---------------------------------------------------------------------------
// Protocol scoring
// ---------------------------------------------------------------------------

/**
 * Score how well a Brit protocol matches a study.
 *
 * Returns 0–100 where 100 is a perfect match on every declared identifier.
 * Only STUDY-type content elements contribute actual match weight; SERIES and
 * IMAGE elements increment the denominator but cannot be matched client-side.
 */
export function scoreProtocol(study: Study, protocol: BritProtocol): number {
  if (!protocol.content?.elements?.length) return 0;

  let matched = 0;
  let total = 0;

  for (const element of protocol.content.elements) {
    if (element.ctype === 'STUDY') {
      for (const ident of element.identifiers) {
        total++;
        if (identifierMatches(study, ident)) matched++;
      }
    } else if (element.ctype === 'SERIES' || element.ctype === 'IMAGE') {
      // Non-study elements count toward total but can't be matched here.
      total += element.identifiers.length;
    }
  }

  if (total === 0) return 0;
  return Math.round((matched / total) * 100);
}

/** Check whether a single Brit identifier matches a study. */
function identifierMatches(
  study: Study,
  ident: { type: string; val?: string },
): boolean {
  const val = ident.val;
  if (!val) return false;

  switch (ident.type) {
    case 'MODALITY':
      return study.mod?.toUpperCase() === val.toUpperCase();

    case 'BODYPART':
      return study.bod?.toUpperCase() === val.toUpperCase();

    case 'STUDYDESCRIPTION':
      return study.descr?.toUpperCase().includes(val.toUpperCase()) ?? false;

    default:
      return false;
  }
}

// ---------------------------------------------------------------------------
// Best-protocol selection (ports GWT ProtocolExecutor.getDefault())
// ---------------------------------------------------------------------------

/**
 * Find the best-matching protocol for a study.
 *
 * The algorithm mirrors the legacy GWT `ProtocolExecutor.getDefault()` logic:
 *
 * 1. Look for a named default (`default-{modality}` or
 *    `default-{modality}-{bodypart}`), lowercased.
 * 2. If a named default exists, exclude all other `default-*` protocols from
 *    the scoring pool (they're noise for a different modality/body-part).
 * 3. Score remaining candidates. If the best score meets the threshold (75
 *    when a named default exists, 50 otherwise), return it.
 * 4. If nothing meets the threshold but a named default exists, return it as
 *    the fallback.
 * 5. Otherwise return `null`.
 */
export function getBestProtocol(
  study: Study,
  protocols: BritProtocol[],
): BritProtocol | null {
  if (protocols.length === 0) return null;

  const namedDefault = findNamedDefault(study, protocols);
  const threshold = namedDefault ? 75 : 50;

  // Build the candidate list: if a named default was found, drop other
  // default-* protocols.  The named default itself stays in the pool so
  // it can compete on score.
  const candidates = namedDefault
    ? protocols.filter(
        (p) => p === namedDefault || !p.id.toLowerCase().startsWith('default-'),
      )
    : protocols;

  let best: BritProtocol | null = null;
  let bestScore = -1;

  for (const protocol of candidates) {
    const score = scoreProtocol(study, protocol);
    if (score > bestScore) {
      bestScore = score;
      best = protocol;
    }
  }

  if (bestScore >= threshold && best) return best;

  // Fallback to the named default when nothing hit the threshold.
  return namedDefault ?? null;
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/**
 * Look for a named default protocol for the study's modality (and optionally
 * body part).  The more-specific `default-{mod}-{bodypart}` wins.
 */
function findNamedDefault(
  study: Study,
  protocols: BritProtocol[],
): BritProtocol | null {
  const mod = study.mod?.toLowerCase();
  if (!mod) return null;

  const bod = study.bod?.toLowerCase();

  // Try most-specific first: default-ct-chest
  if (bod) {
    const specific = protocols.find(
      (p) => p.id.toLowerCase() === `default-${mod}-${bod}`,
    );
    if (specific) return specific;
  }

  // Fall back to modality-only: default-ct
  return protocols.find((p) => p.id.toLowerCase() === `default-${mod}`) ?? null;
}
